"use client";

import { useMemo, useState, type ComponentType, type CSSProperties } from "react";
import {
  SiAnthropic,
  SiBytedance,
  SiClaude,
  SiDeepseek,
  SiElevenlabs,
  SiGooglegemini,
  SiKuaishou,
  SiMetaai,
  SiMistralai,
  SiSuno,
} from "react-icons/si";
import { ImageIcon, Sparkles, Video, Waves } from "lucide-react";
import type { LandingDict } from "@/lib/landingI18n";

type ModelCategory = "llm" | "image" | "video" | "audio";
type ModelTab = "all" | ModelCategory;

interface ModelItem {
  name: string;
  family: string;
  category: ModelCategory;
  color: string;
  icon?: ComponentType<{ size?: number; className?: string; style?: CSSProperties }>;
  mark?: string;
}

const MODELS: ModelItem[] = [
  { name: "GPT / OpenAI", family: "Reasoning, chat, tools", category: "llm", color: "#111111", mark: "AI" },
  { name: "Claude", family: "Anthropic language models", category: "llm", color: "#d97757", icon: SiClaude },
  { name: "Gemini", family: "Google multimodal LLM", category: "llm", color: "#4285f4", icon: SiGooglegemini },
  { name: "DeepSeek", family: "Reasoning and coding", category: "llm", color: "#4d6bfe", icon: SiDeepseek },
  { name: "Mistral", family: "Fast European LLMs", category: "llm", color: "#fa520f", icon: SiMistralai },
  { name: "Llama / Meta AI", family: "Open model family", category: "llm", color: "#0b5cff", icon: SiMetaai },
  { name: "GPT Image", family: "Image generation and editing", category: "image", color: "#111111", mark: "IMG" },
  { name: "Nano Banana", family: "Google image generation", category: "image", color: "#fbbc04", icon: SiGooglegemini },
  { name: "Flux Kontext", family: "Image generation and editing", category: "image", color: "#ff7a1a", icon: ImageIcon },
  { name: "Midjourney", family: "Creative image generation", category: "image", color: "#111111", mark: "MJ" },
  { name: "Veo", family: "Google video generation", category: "video", color: "#34a853", icon: SiGooglegemini },
  { name: "Kling", family: "Kuaishou video models", category: "video", color: "#111111", icon: SiKuaishou },
  { name: "Seedance", family: "ByteDance video models", category: "video", color: "#111111", icon: SiBytedance },
  { name: "Runway", family: "Video generation and editing", category: "video", color: "#111111", icon: Video },
  { name: "Suno", family: "Music and songs", category: "audio", color: "#ff5f1f", icon: SiSuno },
  { name: "ElevenLabs", family: "Voice and speech", category: "audio", color: "#111111", icon: SiElevenlabs },
  { name: "Chimege", family: "Mongolian speech and voice", category: "audio", color: "#ff7a1a", mark: "CH" },
  { name: "Sound / TTS", family: "Speech, music, effects", category: "audio", color: "#ff7a1a", icon: Waves },
  { name: "Anthropic", family: "Claude provider family", category: "llm", color: "#191919", icon: SiAnthropic },
];

const CATEGORY_ICON: Record<ModelCategory, ComponentType<{ size?: number; className?: string }>> = {
  llm: Sparkles,
  image: ImageIcon,
  video: Video,
  audio: Waves,
};

export default function ModelShowcase({ content }: { content: LandingDict["aiModels"] }) {
  const [active, setActive] = useState<ModelTab>("all");

  const tabs = useMemo(
    () => [
      { key: "all" as const, label: content.tabs.all },
      { key: "llm" as const, label: content.tabs.llm },
      { key: "image" as const, label: content.tabs.image },
      { key: "video" as const, label: content.tabs.video },
      { key: "audio" as const, label: content.tabs.audio },
    ],
    [content.tabs],
  );

  const visibleModels = active === "all" ? MODELS : MODELS.filter((model) => model.category === active);

  return (
    <div>
      <div className="flex flex-wrap justify-center gap-2" role="tablist" aria-label={content.heading}>
        {tabs.map((tab) => (
          <button
            key={tab.key}
            type="button"
            role="tab"
            aria-selected={active === tab.key}
            onClick={() => setActive(tab.key)}
            className={`rounded-full px-4 py-2.5 text-sm font-medium transition ${
              active === tab.key
                ? "bg-[var(--af-accent)] text-black"
                : "border border-[var(--af-border)] text-[var(--af-muted)] hover:border-[var(--af-accent)] hover:text-[var(--af-text)]"
            }`}
          >
            {tab.label}
          </button>
        ))}
      </div>

      <div className="mt-8 grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
        {visibleModels.map((model, index) => {
          const Logo = model.icon;
          const CategoryIcon = CATEGORY_ICON[model.category];

          return (
            <div
              key={`${model.category}-${model.name}`}
              className="af-fade-in af-lift flex items-center gap-4 rounded-2xl border border-[var(--af-border)] bg-[var(--af-bg)] p-4"
              style={{ animationDelay: `${index * 45}ms`, boxShadow: "var(--af-shadow)" }}
            >
              <div
                className="flex h-12 w-12 shrink-0 items-center justify-center rounded-2xl border border-[var(--af-border)] bg-[var(--af-bg-soft)] text-[var(--af-text)]"
                aria-hidden="true"
              >
                {Logo ? (
                  <Logo size={25} className="model-logo-pop" style={{ color: model.color }} />
                ) : (
                  <span className="model-logo-pop af-nums text-[11px] font-bold" style={{ color: model.color }}>
                    {model.mark}
                  </span>
                )}
              </div>
              <div className="min-w-0 flex-1">
                <div className="flex items-center gap-2">
                  <h3 className="truncate text-[15px] font-semibold text-[var(--af-text)]">{model.name}</h3>
                  <CategoryIcon size={13} className="shrink-0 text-[var(--af-accent)]" />
                </div>
                <p className="mt-1 text-[12.5px] leading-snug text-[var(--af-muted)]">{model.family}</p>
              </div>
            </div>
          );
        })}
      </div>

    </div>
  );
}
